import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, DownloadSimple, SpinnerGap, CheckCircle,
  EnvelopeSimple, User, Buildings, FileText
} from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DownloadModal = ({ isOpen, onClose, resource, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Save lead to database
      await axios.post(`${API}/resource-download`, {
        ...formData,
        resource_id: resource.id,
        resource_title: resource.title,
        resource_type: resource.type
      });

      setSuccess(true);
      
      // Call success callback after short delay
      setTimeout(() => {
        onSuccess && onSuccess(resource);
        // Reset form for next use
        setTimeout(() => {
          setSuccess(false);
          setFormData({ name: '', email: '', organization: '' });
          onClose();
        }, 500);
      }, 1500);

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="download-modal-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="download-modal"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary/80 px-6 py-5 relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              data-testid="modal-close-button"
            >
              <X weight="bold" className="w-4 h-4 text-white" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <FileText weight="fill" className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Download Resource</h3>
                <p className="text-white/70 text-sm">Get instant access</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle weight="fill" className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">Download Starting!</h3>
                <p className="text-muted-foreground">
                  Your download will begin automatically. Check your email for additional resources.
                </p>
              </motion.div>
            ) : (
              <>
                {/* Resource Preview */}
                <div className="bg-muted/50 rounded-xl p-4 mb-6">
                  <p className="text-sm text-muted-foreground mb-1">You're downloading:</p>
                  <p className="font-semibold text-foreground">{resource?.title}</p>
                  <span className="inline-block mt-2 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    {resource?.type?.charAt(0).toUpperCase() + resource?.type?.slice(1)}
                  </span>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        Full Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder="John Smith"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full h-12 pl-12 pr-4 bg-white border-2 border-border rounded-xl text-foreground focus:outline-none focus:border-primary transition-colors"
                          required
                          data-testid="download-name"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        Work Email *
                      </label>
                      <div className="relative">
                        <EnvelopeSimple className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <input
                          type="email"
                          placeholder="john@hospice.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full h-12 pl-12 pr-4 bg-white border-2 border-border rounded-xl text-foreground focus:outline-none focus:border-primary transition-colors"
                          required
                          data-testid="download-email"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        Organization *
                      </label>
                      <div className="relative">
                        <Buildings className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder="Your hospice organization"
                          value={formData.organization}
                          onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                          className="w-full h-12 pl-12 pr-4 bg-white border-2 border-border rounded-xl text-foreground focus:outline-none focus:border-primary transition-colors"
                          required
                          data-testid="download-organization"
                        />
                      </div>
                    </div>
                  </div>

                  {error && (
                    <p className="text-red-600 text-sm mt-4">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full mt-6 bg-primary text-white font-semibold h-12 rounded-xl flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-50"
                    data-testid="download-submit"
                  >
                    {loading ? (
                      <>
                        <SpinnerGap className="w-5 h-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <DownloadSimple weight="bold" className="w-5 h-5" />
                        Download Now
                      </>
                    )}
                  </button>
                </form>

                <p className="text-xs text-muted-foreground text-center mt-4">
                  By downloading, you agree to receive occasional emails from Noble Hospice Solutions. 
                  Unsubscribe anytime.
                </p>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DownloadModal;
